import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as Context from 'Context';
import {
  ActionMethod,
  ActionOrigin,
  LegacyAssaySchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {trackOpenedRelevantTemplatesTab} from 'nav/analytics/RelevantTemplatesAnalytics';
import notifications from 'notifications';
import {Organization as OrganizationType} from 'orgs/OrganizationConstants';
import {getStore} from 'redux-stores/Store';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssaySchemaPermissionSettings from 'results/AssaySchemaPermissionSettings';
import {RELEVANT_TEMPLATES_TAB_NAME} from 'schemas/relevant-templates/RelevantTemplatesConstants';
import RelevantTemplatesTab from 'schemas/relevant-templates/RelevantTemplatesTab';
import {AssaySchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import EntityStore from 'stores/EntityStore';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {capitalize} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import {SCHEMA_TYPE_NAME} from './AssaySchemaConstants';
import * as AssaySchemaDAO from './AssaySchemaDAO';
import {ExistingAssaySchemaEditorProps} from './AssaySchemaEditorTypes';
import {getSpecForRequest, AssayRunSchemaSpecOverrides} from './AssaySchemaEditorUtils';
import AssaySchemaMetadataSettings from './AssaySchemaMetadataSettings';

enum Tabs {
  ASSAY_SCHEMA_GUI_EDITOR = 'ASSAY_SCHEMA_GUI_EDITOR',
  ASSAY_SCHEMA_ACCESS_POLICIES = 'ACCESS ASSAY_SCHEMA_ACCESS_POLICIES',
  ASSAY_SCHEMA_RELEVANT_TEMPLATES = 'ASSAY_SCHEMA_RELEVANT_TEMPLATES',
}

function trackLabAutomationConfigUpdates(props: ExistingAssaySchemaEditorProps): void {
  const metadata = {
    action_origin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
    action_method: ActionMethod.SETTINGS_EDIT,
    schema_type: props.schemaType,
    schema_id: props.schema.id,
    session_id: props.sessionId,
  };
  if (props.hasUnsavedInputFileConfigData) {
    analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.UPDATED_INPUT_FILE_CONFIG, metadata);
  }
  if (props.hasUnsavedOutputFileConfigData) {
    analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.UPDATED_OUTPUT_FILE_CONFIG, metadata);
  }
}

export default function ExistingAssaySchemaEditor(props: ExistingAssaySchemaEditorProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.ASSAY_SCHEMA_GUI_EDITOR);

  function trackOpenTabEvent(tab: Tabs): void {
    if (tab === Tabs.ASSAY_SCHEMA_RELEVANT_TEMPLATES) {
      trackOpenedRelevantTemplatesTab({
        schemaType: props.schemaType,
        schemaId: props.schema.id,
        isAdmin: EntityStore.currentUser().organizations.some((org: OrganizationType) => org.admin),
      });
    }
  }

  function handleSetActiveTab(tab: Tabs): void {
    setActiveTab(tab);
    trackOpenTabEvent(tab);
  }

  const updateAssaySchemaMetadata = ({
    name,
    sqlIdentifier,
    sessionId,
  }: {
    name?: string;
    sqlIdentifier?: string;
    sessionId: string;
  }): void => {
    withLoadingScreen(
      AssaySchemaDAO.updateAssaySchemaMetadata({
        schemaId: props.schema.id,
        name,
        sqlIdentifier,
        sessionId,
      })
        .then(() => {
          notifications.success('Updated schema successfully');
        })
        .catch((response) => {
          notifications.notifyError(response);
        }),
      'Updating...'
    );
  };

  const updateAssaySchema = ({
    name,
    sqlIdentifier,
    spec,
    draft,
    overrides,
  }: {
    name?: string;
    sqlIdentifier?: string;
    spec?: AssaySchemaSpec | null;
    draft?: AssaySchemaSpec | null;
    overrides?: AssayRunSchemaSpecOverrides | null;
  } = {}): void => {
    spec = spec ?? getSpecForRequest(props, overrides);
    if (spec == null) {
      return;
    }
    Context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
        },
      },
      null,
      () => {
        withLoadingScreen(
          AssaySchemaActions.updateAssaySchema({
            schemaId: props.schema.id,
            schemaName: name ?? props.name,
            sqlIdentifier: BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME
              ? (sqlIdentifier ?? props.sqlIdentifier)
              : null,
            spec,
            sessionId: props.sessionId,
            isSpecJsonFormat: false,
            publishDefaultDataTableConfig: props.publishDefaultDataTableConfig,
          }).catch(() => {
            getStore().dispatch(
              SchemaConfigReduxActions.schemaSpecResetToDraft(JSON.stringify(draft ?? spec))
            );
          }),
          'Updating...'
        );
      }
    );
    trackLabAutomationConfigUpdates(props);
  };

  const shouldRenderRelevantTemplatesTab =
    BenchlingConfig.SCHEMAS_UI_ENABLE_RELEVANT_TEMPLATES_TAB &&
    props.schemaType === LegacyAssaySchemaType.ASSAY_RESULT;

  return (
    <TabContainer
      activeTab={activeTab}
      onRequestTabChange={handleSetActiveTab}
      className="u-flex-grow u-min-width-zero"
    >
      <Tab
        tabKey={Tabs.ASSAY_SCHEMA_GUI_EDITOR}
        title={`${capitalize(SCHEMA_TYPE_NAME[props.schemaType])} Settings`}
      >
        <AssaySchemaMetadataSettings
          {...props}
          schemaId={props.schema.id}
          schema={props.schema}
          onUpdateAssaySchema={updateAssaySchema}
          onUpdateAssaySchemaMetadata={updateAssaySchemaMetadata}
        />
      </Tab>
      {BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS && (
        <Tab tabKey={Tabs.ASSAY_SCHEMA_ACCESS_POLICIES} title="Access Policies">
          <AssaySchemaPermissionSettings assaySchemaId={props.schema.id} />
        </Tab>
      )}
      {shouldRenderRelevantTemplatesTab && (
        <Tab tabKey={Tabs.ASSAY_SCHEMA_RELEVANT_TEMPLATES} title={RELEVANT_TEMPLATES_TAB_NAME}>
          <RelevantTemplatesTab
            schemaId={props.schema.id}
            schemaOrgUrlId={props.schema.organization.url_identifier}
          />
        </Tab>
      )}
    </TabContainer>
  );
}
